import {
  Alert,
  Badge,
  Box,
  Button,
  Card,
  Code,
  Group,
  Image,
  PasswordInput,
  Stack,
  Switch,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { IconAlertTriangle, IconCheck, IconKey, IconTrash } from '@tabler/icons-react';
import { startRegistration } from '@simplewebauthn/browser';
import type { PublicKeyCredentialCreationOptionsJSON } from '@simplewebauthn/browser';
import { useState } from 'react';
import { ApiError } from '../api/client';
import { humanizeWebAuthnError } from '../api/webauthn-errors';
import {
  useConfirmTotp,
  useDisableTotp,
  useMfaSettings,
  useMyMfa,
  usePasskeyRegistrationOptions,
  useRemovePasskey,
  useSetupTotp,
  useUpdateMfaSettings,
  useVerifyPasskeyRegistration,
} from '../api/mfa';
import { useChangePassword, useMe } from '../api/session';

const MIN_PASSWORD_LENGTH = 12;

function PasswordCard() {
  const change = useChangePassword();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);

    if (newPassword.length < MIN_PASSWORD_LENGTH) {
      setError(`New password must be at least ${MIN_PASSWORD_LENGTH} characters.`);
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('New password and confirmation do not match.');
      return;
    }
    if (newPassword === currentPassword) {
      setError('New password must differ from your current password.');
      return;
    }

    change.mutate(
      { currentPassword, newPassword },
      {
        onSuccess: () => {
          setSuccess(true);
          setCurrentPassword('');
          setNewPassword('');
          setConfirmPassword('');
        },
        onError: (err) => {
          if (err instanceof ApiError) {
            if (err.status === 401) setError('Current password is incorrect.');
            else if (err.status === 400)
              setError(
                `New password must be at least ${MIN_PASSWORD_LENGTH} characters and meet the policy.`,
              );
            else setError('Could not update password. Please try again.');
          } else {
            setError('Could not update password. Please try again.');
          }
        },
      },
    );
  };

  return (
    <Card shadow="sm" padding="lg" maw={520}>
      <Box component="form" onSubmit={handleSubmit}>
        <Stack gap="md">
          <Title order={4}>Change password</Title>
          <Text size="sm" c="dimmed">
            Minimum {MIN_PASSWORD_LENGTH} characters. After saving, your existing session stays
            active — you do not need to sign in again.
          </Text>

          {error && (
            <Alert color="red" icon={<IconAlertTriangle size={18} />} role="alert">
              {error}
            </Alert>
          )}
          {success && (
            <Alert color="green" icon={<IconCheck size={18} />} role="status">
              Password updated.
            </Alert>
          )}

          <PasswordInput
            label="Current password"
            autoComplete="current-password"
            required
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.currentTarget.value)}
            disabled={change.isPending}
          />
          <PasswordInput
            label="New password"
            autoComplete="new-password"
            required
            value={newPassword}
            onChange={(e) => setNewPassword(e.currentTarget.value)}
            disabled={change.isPending}
          />
          <PasswordInput
            label="Confirm new password"
            autoComplete="new-password"
            required
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.currentTarget.value)}
            disabled={change.isPending}
          />
          <Button type="submit" loading={change.isPending}>
            Update password
          </Button>
        </Stack>
      </Box>
    </Card>
  );
}

function TotpSection() {
  const mfa = useMyMfa();
  const setup = useSetupTotp();
  const confirm = useConfirmTotp();
  const disable = useDisableTotp();
  const [code, setCode] = useState('');
  const [setupData, setSetupData] = useState<{ qrDataUrl: string; secret: string } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const enrolled = mfa.data?.totp.enrolled === true;

  function handleStart() {
    setError(null);
    setup.mutate(undefined, {
      onSuccess: (data) => setSetupData({ qrDataUrl: data.qrDataUrl, secret: data.secret }),
      onError: () => setError('Could not start TOTP enrolment.'),
    });
  }

  function handleConfirm(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    confirm.mutate(
      { code: code.trim() },
      {
        onSuccess: () => {
          setSetupData(null);
          setCode('');
        },
        onError: (err) => {
          if (err instanceof ApiError && err.status === 400) setError('Invalid code. Try again.');
          else setError('Could not confirm code.');
        },
      },
    );
  }

  return (
    <Card withBorder padding="lg" maw={520}>
      <Stack gap="md">
        <Group justify="space-between">
          <Title order={5}>Authenticator app (TOTP)</Title>
          {enrolled && (
            <Badge color="green" variant="light">
              Enrolled
            </Badge>
          )}
        </Group>
        <Text size="sm" c="dimmed">
          Use Google Authenticator, 1Password, Bitwarden, or any other TOTP-compatible app to
          generate a 6-digit code at sign in.
        </Text>

        {error && (
          <Alert color="red" icon={<IconAlertTriangle size={18} />}>
            {error}
          </Alert>
        )}

        {!enrolled && !setupData && (
          <Button onClick={handleStart} loading={setup.isPending}>
            Set up authenticator
          </Button>
        )}

        {setupData && (
          <Box component="form" onSubmit={handleConfirm}>
            <Stack gap="md">
              <Image src={setupData.qrDataUrl} alt="Scan with your authenticator app" w={180} />
              <Text size="xs" c="dimmed">
                Or enter this secret manually: <Code>{setupData.secret}</Code>
              </Text>
              <TextInput
                label="Enter the 6-digit code from your app"
                value={code}
                onChange={(e) => setCode(e.currentTarget.value)}
                autoComplete="one-time-code"
                inputMode="numeric"
                maxLength={6}
                required
              />
              <Group justify="flex-end" gap="sm">
                <Button variant="default" onClick={() => setSetupData(null)}>
                  Cancel
                </Button>
                <Button type="submit" loading={confirm.isPending}>
                  Confirm
                </Button>
              </Group>
            </Stack>
          </Box>
        )}

        {enrolled && !setupData && (
          <Group gap="sm">
            <Button
              color="red"
              variant="subtle"
              onClick={() => disable.mutate()}
              loading={disable.isPending}
            >
              Remove TOTP
            </Button>
          </Group>
        )}
      </Stack>
    </Card>
  );
}

function PasskeySection() {
  const mfa = useMyMfa();
  const options = usePasskeyRegistrationOptions();
  const verify = useVerifyPasskeyRegistration();
  const remove = useRemovePasskey();
  const [deviceName, setDeviceName] = useState('');
  const [error, setError] = useState<string | null>(null);

  const passkeys = mfa.data?.passkeys ?? [];

  async function handleAdd() {
    setError(null);
    try {
      const opts = await options.mutateAsync();
      const reg = await startRegistration({
        optionsJSON: opts as PublicKeyCredentialCreationOptionsJSON,
      });
      await verify.mutateAsync({ response: reg, deviceName: deviceName || undefined });
      setDeviceName('');
    } catch (err) {
      setError(humanizeWebAuthnError(err, 'register'));
    }
  }

  return (
    <Card withBorder padding="lg" maw={520}>
      <Stack gap="md">
        <Group justify="space-between">
          <Title order={5}>Passkeys (Touch ID, Face ID, security keys)</Title>
          {passkeys.length > 0 && (
            <Badge color="green" variant="light">
              {passkeys.length} enrolled
            </Badge>
          )}
        </Group>
        <Text size="sm" c="dimmed">
          Sign in using a biometric authenticator built into your device, or a hardware security
          key.
        </Text>

        {error && (
          <Alert color="red" icon={<IconAlertTriangle size={18} />}>
            {error}
          </Alert>
        )}

        {passkeys.length > 0 && (
          <Stack gap={4}>
            {passkeys.map((p) => (
              <Group key={p.id} justify="space-between" gap="xs">
                <Group gap="xs" wrap="nowrap">
                  <IconKey size={16} />
                  <Text size="sm">{p.deviceName ?? 'Unnamed passkey'}</Text>
                  <Text size="xs" c="dimmed">
                    Added {new Date(p.createdAt).toLocaleDateString()}
                  </Text>
                </Group>
                <Button
                  variant="subtle"
                  color="red"
                  size="compact-xs"
                  leftSection={<IconTrash size={12} />}
                  onClick={() => remove.mutate(p.id)}
                  loading={remove.isPending}
                >
                  Remove
                </Button>
              </Group>
            ))}
          </Stack>
        )}

        <TextInput
          label="Device label (optional)"
          placeholder="e.g. MacBook Pro Touch ID"
          value={deviceName}
          onChange={(e) => setDeviceName(e.currentTarget.value)}
        />
        <Button onClick={handleAdd} loading={options.isPending || verify.isPending}>
          Add passkey
        </Button>
      </Stack>
    </Card>
  );
}

function GlobalMfaSection() {
  const me = useMe();
  const settings = useMfaSettings();
  const update = useUpdateMfaSettings();
  const [error, setError] = useState<string | null>(null);

  if (me.data?.role !== 'admin') return null;

  function handleToggle(checked: boolean) {
    setError(null);
    update.mutate(
      { requireMfa: checked },
      {
        onError: () => setError('Could not update the setting.'),
      },
    );
  }

  return (
    <Card withBorder padding="lg" maw={520}>
      <Stack gap="sm">
        <Title order={5}>Require MFA for all admin users</Title>
        <Text size="sm" c="dimmed">
          When on, admin users who haven&apos;t enrolled an MFA factor will be blocked from signing
          in. Enrolled users will always be challenged for a factor, regardless of this toggle.
        </Text>
        {error && (
          <Alert color="red" icon={<IconAlertTriangle size={18} />}>
            {error}
          </Alert>
        )}
        <Switch
          label={settings.data?.requireMfa ? 'Required' : 'Optional'}
          checked={settings.data?.requireMfa ?? false}
          onChange={(e) => handleToggle(e.currentTarget.checked)}
          disabled={settings.isPending || update.isPending}
        />
      </Stack>
    </Card>
  );
}

export function SettingsPage() {
  return (
    <Stack gap="lg">
      <Title order={2}>Settings</Title>

      <PasswordCard />

      <Stack gap="md">
        <Title order={3}>Multi-factor authentication</Title>
        <Text size="sm" c="dimmed" maw={520}>
          Enrol at least one factor below. When enrolled you&apos;ll be challenged for it at sign
          in. Passkeys are preferred — they&apos;re phishing-resistant — with TOTP as a fallback.
        </Text>
        <PasskeySection />
        <TotpSection />
        <GlobalMfaSection />
      </Stack>
    </Stack>
  );
}
